import { existsFilter } from '@iotv/core-services';
import { isValidBusinessObject } from '@iotv/datamodel-core';
import { getObjectHash, isHistoryObject } from '@iotv/iotv-v3-types';
import { createContext } from 'react';
import { defaultReducerState, operationalStatusReducer } from '../../OperationalStatus/Reducers/OperationalStatusReducer';
const debug = process.env.REACT_APP_DEBUG && false;
const isEntityStateOb = (ob) => {
    const { device, deviceConfig, entity, recentStateHistory } = ob ?? {};
    const entityValid = isValidBusinessObject(entity);
    const deviceValid = device === null || isValidBusinessObject(device);
    const deviceConfigValid = deviceConfig === null || isValidBusinessObject(deviceConfig);
    const recentStateHistoryValid = recentStateHistory === null || isHistoryObject(recentStateHistory);
    return entityValid && deviceValid && deviceConfigValid && recentStateHistoryValid;
};
export const defaultGroupStateReducerState = {
    fromDateValue: null,
    toDateValue: null,
    entityStateMap: {},
    rows: null,
    useReportedRequested: 'REPORTED',
};
const isUpdateEntitiesAction = (x) => {
    const c = x;
    return !!c && c.type === 'UpdateEntityStates' && c.update?.entityStates instanceof Array && c.update?.entityStates.every(isEntityStateOb);
};
const isClearEntitiesAction = (x) => {
    const c = x;
    return !!c && c.type === 'ClearEntityStates' && c.update === undefined;
};
const isSetDatesAction = (x) => !!x && x.type === 'SetDates' && x.update?.fromDateValue && x.update.toDateValue;
const isSetReportedRequestAction = (x) => !!x && x.type === 'SetReportedRequest';
/** emits array or DateRange objects which each row in top level array being produced by a single entity, this is to allow sorting later */
export const groupStateReducer = (prev, action) => {
    const next = { ...prev };
    if (isUpdateEntitiesAction(action)) {
        const { entityStates: entities } = action.update;
        for (let { entity, recentStateHistory, device, deviceConfig } of entities) {
            const { entityStateMap } = next;
            const { sk: key } = entity;
            if (!entityStateMap[key]) {
                entityStateMap[key] = { entity, device: null, deviceConfig: null, recentStateHistory: null };
            }
            const existing = entityStateMap[key];
            const update = {
                entity,
                recentStateHistory: recentStateHistory ?? existing.recentStateHistory,
                device: device ?? existing.device,
                deviceConfig: deviceConfig ?? existing.deviceConfig,
            };
            debug && console.log(`INFO: UpdateEntitiesAction updating ${key} with`, update);
            next.entityStateMap[key] = update;
        }
    }
    else if (isClearEntitiesAction(action)) {
        next.entityStateMap = {};
        next.rows = null;
        debug && console.log('INFO: ClearEntitiesAction cleared entities');
    }
    else if (isSetDatesAction(action)) {
        const { fromDateValue, toDateValue } = action.update;
        next.fromDateValue = fromDateValue;
        next.toDateValue = toDateValue;
    }
    else if (isSetReportedRequestAction(action)) {
        next.useReportedRequested = action.update.useReportedRequested;
        debug && console.log(`INFO: SetReportedRequestAction updating  with`, action.update.useReportedRequested);
    }
    else {
        debug && console.log(`groupStateReducer fell through with`, { action });
    }
    const reportedRequestedDiff = prev.useReportedRequested !== next.useReportedRequested;
    const fromDateDiff = !prev.fromDateValue || !prev.fromDateValue.isSame(next.fromDateValue);
    const toDateDiff = !prev.toDateValue || !prev.toDateValue.isSame(next.toDateValue);
    const keysDiff = getObjectHash(Object.keys(prev.entityStateMap) ?? {}) === getObjectHash(Object.keys(next.entityStateMap) ?? {});
    if (fromDateDiff || toDateDiff || keysDiff || reportedRequestedDiff) {
        const combinedRows = Object.values(next.entityStateMap)
            .filter(({ entity, recentStateHistory }) => {
            const { config: currentSequentialSchedules } = entity ?? {};
            return !!currentSequentialSchedules;
        })
            .map(({ entity, recentStateHistory, device, deviceConfig }) => {
            const { rows } = operationalStatusReducer({ ...defaultReducerState, toDateValue: next.toDateValue, fromDateValue: next.fromDateValue }, {
                entity,
                device,
                deviceConfig,
                recentStateHistory,
                fromDateValue: next.fromDateValue,
                toDateValue: next.toDateValue,
                useReportedRequested: next.useReportedRequested,
            });
            return rows;
        })
            .filter(existsFilter);
        next.rows = combinedRows;
    }
    return next;
};
export const GroupOperationalStateReducerContext = createContext([
    defaultGroupStateReducerState,
    (next) => {
        throw new Error('Reducer not initialized');
    },
]);
